import React, { useEffect, useRef, useState } from 'react';

/**
 * BottomSheet — native-app-style modal that slides up from the bottom.
 * Features: backdrop tap-to-close, drag-handle + drag-down-to-dismiss, Esc to close,
 * body-scroll-lock, rounded top, safe-area aware, scrollable body.
 *
 * Props:
 *   open      {boolean}
 *   onClose   {function}
 *   title     {string}   optional header title
 *   children  {node}     sheet body
 *   maxHeight {string}   default '85vh'
 */
const BottomSheet = ({ open, onClose, title, children, maxHeight = '85vh' }) => {
  const [dragY, setDragY] = useState(0);     // live drag offset
  const startY = useRef(null);
  const hasDragged = useRef(false);          // once dragged, don't replay the open slide-up

  // Esc to close + lock background scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // reset drag + allow open slide-up to play once, when (re)opened
  useEffect(() => { if (open) { setDragY(0); hasDragged.current = false; } }, [open]);

  if (!open) return null;

  // Drag-down-to-dismiss (handle area only — body keeps scrolling)
  const onHandleTouchStart = (e) => { startY.current = e.touches[0].clientY; };
  const onHandleTouchMove = (e) => {
    if (startY.current === null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) { hasDragged.current = true; setDragY(dy); } // only allow dragging down
  };
  const onHandleTouchEnd = () => {
    if (dragY > 90) { onClose && onClose(); }   // dragged far enough → dismiss
    else { setDragY(0); }                        // snap back
    startY.current = null;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        style={{ animation: 'bsFade 0.2s ease' }}
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        className="relative w-full bg-white rounded-t-2xl shadow-2xl flex flex-col"
        style={{
          maxHeight,
          transform: `translateY(${dragY}px)`,
          transition: startY.current === null ? 'transform 0.25s cubic-bezier(0.32,0.72,0,1)' : 'none',
          // play the slide-up only on first open; after a drag, snap back via transition (no offscreen flicker)
          animation: hasDragged.current ? undefined : 'bsUp 0.28s cubic-bezier(0.32,0.72,0,1)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Drag handle (draggable to dismiss) */}
        <div
          className="flex justify-center pt-2.5 pb-1.5 shrink-0 cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: 'none' }}
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
        >
          <span className="h-1.5 w-10 rounded-full bg-gray-300" />
        </div>

        {/* Header — X always rendered (close affordance) even if no title */}
        {(title || onClose) && (
          <div className="flex items-center justify-between px-4 pb-2.5 shrink-0 border-b border-gray-100">
            <h3 className="text-base font-bold text-gray-900 pr-2">{title || ''}</h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 active:scale-90 transition-transform shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Scrollable body — flex-1 + min-h-0 so it shrinks and its own scrollbar engages
            (otherwise tall content overflows past 85vh and the bottom is unreachable) */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3" style={{ WebkitOverflowScrolling: 'touch' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottomSheet;
